import { Injectable } from '@angular/core';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable()
export class Base64HttpInterceptor implements HttpInterceptor {
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.isApiRequest(request.url)) {
            return next.handle(request);
        }

        const encodedRequest = request.clone({ body: this.transform(request.body, true) });
        return next.handle(encodedRequest).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    return event.clone({ body: this.transform(event.body, false) });
                }
                return event;
            })
        );
    }

    private isApiRequest(url: string): boolean {
        const endpoint = environment.APIEndpoint;
        const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
        if (endpoint === '/') {
            const requestUrl = new URL(url, origin);
            return requestUrl.origin === origin;
        }
        const absoluteEndpoint = new URL(endpoint, origin);
        const absoluteUrl = new URL(url, origin);
        return absoluteUrl.origin === absoluteEndpoint.origin &&
            absoluteUrl.pathname.indexOf(absoluteEndpoint.pathname) === 0;
    }

    private transform(value: any, encode: boolean): any {
        if (value === null || value === undefined) {
            return value;
        }
        if (typeof value === 'string') {
            return encode ? this.encode(value) : this.decode(value);
        }
        if (value instanceof Date || value instanceof Blob || value instanceof ArrayBuffer ||
            ArrayBuffer.isView(value)) {
            return value;
        }
        if (typeof FormData !== 'undefined' && value instanceof FormData) {
            if (!encode) {
                return value;
            }
            const transformedForm = new FormData();
            value.forEach((entry: string | Blob, key: string) => {
                if (typeof entry === 'string') {
                    transformedForm.append(key, this.encode(entry));
                } else if (typeof File !== 'undefined' && entry instanceof File) {
                    transformedForm.append(key, entry, entry.name);
                } else {
                    transformedForm.append(key, entry);
                }
            });
            return transformedForm;
        }
        if (Array.isArray(value)) {
            return value.map((entry: any) => this.transform(entry, encode));
        }
        if (typeof value === 'object') {
            const transformed: any = {};
            Object.keys(value).forEach((key: string) => {
                transformed[key] = this.transform(value[key], encode);
            });
            return transformed;
        }
        return value;
    }

    private encode(value: string): string {
        const bytes = new TextEncoder().encode(value);
        let binary = '';
        for (let offset = 0; offset < bytes.length; offset += 0x8000) {
            binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(offset, offset + 0x8000)));
        }
        return btoa(binary);
    }

    private decode(value: string): string {
        try {
            const binary = atob(value);
            const bytes = new Uint8Array(binary.length);
            for (let index = 0; index < binary.length; index++) {
                bytes[index] = binary.charCodeAt(index);
            }
            const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
            return this.encode(decoded) === value ? decoded : value;
        } catch (error) {
            return value;
        }
    }
}
